import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { SearchSelect } from '../../shared/ui/SearchSelect';
import { useTabParam } from '../../shared/ui/useTabParam';

type Program = 'exploradores' | 'misioneritas';
interface Group {
  id: string;
  name: string;
  minAge: number | null;
  maxAge: number | null;
}
interface Award {
  id: string;
  kind: 'insignia' | 'rango';
  name: string;
  description: string | null;
}
interface Member {
  personId: string;
  name: string;
  birthDate: string | null;
  groupId: string | null;
  joinedOn: string;
  earned: { awardId: string; earnedOn: string }[];
}
interface View {
  groups: Group[];
  awards: Award[];
  members: Member[];
  canManage: boolean;
  ministryId: string | null;
}

type Info = { title: string; kids: string; one: string; blurb: string; group: string; groups: string; none: string };
const INFO: Record<Program, Info> = {
  exploradores: { title: 'Exploradores del Rey', kids: 'Exploradores', one: 'explorador', blurb: 'Niños y adolescentes: ramas por edad, insignias y rangos.', group: 'rama', groups: 'ramas', none: 'Sin rama' },
  misioneritas: { title: 'Misioneritas', kids: 'Misioneritas', one: 'misionerita', blurb: 'Niñas y adolescentes: grupos por edad, insignias y rangos.', group: 'grupo', groups: 'grupos', none: 'Sin grupo' },
};
const cap = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

const ageOf = (birth: string | null) => {
  if (!birth) return null;
  const b = new Date(`${birth}T12:00:00`);
  const n = new Date();
  return n.getFullYear() - b.getFullYear() - (n < new Date(n.getFullYear(), b.getMonth(), b.getDate()) ? 1 : 0);
};
const ages = (g: Group) => (g.minAge !== null || g.maxAge !== null ? `${g.minAge ?? '…'} a ${g.maxAge ?? '…'} años` : '');
const today = () => new Date().toISOString().slice(0, 10);

/** Asambleas de Dios · Exploradores del Rey o Misioneritas: quién está en cada rama y su avance. */
export function AdYouthPage({ program }: { program: Program }) {
  const me = useMe();
  const churchId = me.activeChurchId;
  const base = `/multichurch/churches/${churchId}/ad/youth/${program}`;
  const data = useAsync((signal) => (churchId ? api.get<View>(base, signal) : Promise.resolve(null)), [base]);
  const [tab, setTab] = useTabParam(['members', 'groups', 'awards'] as const);
  const [msg, setMsg] = useState<string | null>(null);
  const info = INFO[program];
  // Al recargar se sigue mostrando lo último (marcar insignias no parpadea).
  const [last, setLast] = useState<View | null>(null);
  useEffect(() => {
    if (data.status === 'ready') setLast(data.data);
  }, [data]);

  if (!churchId) return <div className="page"><EmptyState title="Elige primero una iglesia en el selector de arriba." /></div>;
  const v = data.status === 'ready' ? data.data : last;

  async function run(action: () => Promise<unknown>) {
    setMsg(null);
    try {
      await action();
      data.reload();
    } catch (e) {
      setMsg(errorMessage(e));
    }
  }

  return (
    <div className="page">
      <header className="page__header">
        <p className="eyebrow">Asambleas de Dios</p>
        <h1>{info.title}</h1>
        <p className="muted">
          {info.blurb} {program === 'misioneritas' ? 'Los grupos' : 'Las ramas'}, insignias y rangos los capturas según el manual de tu distrito.
          {v?.ministryId ? (
            <>
              {' '}
              La asistencia a sus reuniones se lleva en <Link to={`/app/ministerios/${v.ministryId}`}>su ministerio</Link> (Clases).
            </>
          ) : null}
        </p>
      </header>
      {msg ? <Alert>{msg}</Alert> : null}
      {data.status === 'loading' && !v ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {v ? (
        <>
          <div className="tabs tabs--scroll" role="tablist" aria-label={info.title}>
            {(
              [
                ['members', `${info.kids} (${v.members.length})`],
                ['groups', `${cap(info.groups)} (${v.groups.length})`],
                ['awards', `Insignias y rangos (${v.awards.length})`],
              ] as const
            ).map(([k, label]) => (
              <button key={k} type="button" role="tab" aria-selected={tab === k} className={`tabs__tab${tab === k ? ' tabs__tab--active' : ''}`} onClick={() => setTab(k)}>
                {label}
              </button>
            ))}
          </div>
          {tab === 'members' ? <Members base={base} v={v} info={info} run={run} /> : null}
          {tab === 'groups' ? <Groups base={base} v={v} info={info} run={run} /> : null}
          {tab === 'awards' ? <Awards base={base} v={v} run={run} /> : null}
        </>
      ) : null}
    </div>
  );
}

type Run = (action: () => Promise<unknown>) => Promise<void>;

function Members({ base, v, info, run }: { base: string; v: View; info: Info; run: Run }) {
  const [open, setOpen] = useState<string | null>(null);
  const candidates = useAsync(
    (signal) => (v.canManage ? api.get<{ items: { personId: string; name: string; birthDate: string | null }[] }>(`${base}/candidates`, signal).then((r) => r.items) : Promise.resolve([])),
    [base, v.canManage, v.members.length],
  );
  const sections = [...v.groups.map((g) => ({ id: g.id as string | null, title: `${g.name}${ages(g) ? ` · ${ages(g)}` : ''}` })), { id: null, title: info.none }];

  function add(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const personId = String(f.get('personId') ?? '');
    if (!personId) return;
    const groupId = String(f.get('groupId') ?? '') || null;
    e.currentTarget.reset();
    void run(() => api.put(`${base}/members/${personId}`, { groupId }));
  }

  return (
    <>
      {v.canManage ? (
        <Card title={`Inscribir ${info.one}`}>
          <form className="form form--inline" onSubmit={add}>
            <div className="field">
              <label htmlFor="youth-person">Persona</label>
              <SearchSelect
                id="youth-person"
                name="personId"
                options={(candidates.status === 'ready' ? candidates.data : []).map((c) => ({ value: c.personId, label: c.name, ...(ageOf(c.birthDate) !== null ? { hint: `${ageOf(c.birthDate)} años` } : {}) }))}
              />
            </div>
            <div className="field">
              <label htmlFor="youth-group">{cap(info.group)}</label>
              <select id="youth-group" name="groupId" defaultValue="">
                <option value="">{info.none}</option>
                {v.groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </div>
            <Button type="submit" variant="secondary">
              Inscribir
            </Button>
          </form>
          <p className="small muted">Aparecen los miembros de la iglesia y los niños registrados en sus familias.</p>
        </Card>
      ) : null}
      {v.members.length === 0 ? <EmptyState title="Todavía no hay nadie inscrito." /> : null}
      {sections.map((s) => {
        const list = v.members.filter((m) => m.groupId === s.id);
        if (list.length === 0) return null;
        return (
          <Card key={s.id ?? 'none'} title={`${s.title} (${list.length})`}>
            <ul className="list">
              {list.map((m) => {
                const ranks = m.earned.map((e) => v.awards.find((a) => a.id === e.awardId)).filter((a) => a?.kind === 'rango');
                const badges = m.earned.length - ranks.length;
                return (
                  <li key={m.personId} className="list__item youth-member">
                    <button type="button" className="linklike" aria-expanded={open === m.personId} onClick={() => setOpen(open === m.personId ? null : m.personId)}>
                      <strong>{m.name}</strong>
                      <span className="small muted">
                        {[ageOf(m.birthDate) !== null ? `${ageOf(m.birthDate)} años` : null, ranks.length ? ranks[ranks.length - 1]!.name : null, `${badges} ${badges === 1 ? 'insignia' : 'insignias'}`].filter(Boolean).join(' · ')}
                      </span>
                    </button>
                    {open === m.personId ? <Progress base={base} v={v} m={m} info={info} run={run} /> : null}
                  </li>
                );
              })}
            </ul>
          </Card>
        );
      })}
    </>
  );
}

function Progress({ base, v, m, info, run }: { base: string; v: View; m: Member; info: Info; run: Run }) {
  const earned = new Map(m.earned.map((e) => [e.awardId, e.earnedOn]));
  // Al marcar una insignia (o al tocar su fecha) se pregunta la fecha; hoy ya viene puesta.
  const [dating, setDating] = useState<{ awardId: string; date: string } | null>(null);
  const setDate = (awardId: string, earnedOn: string | null) => run(() => api.put(`${base}/members/${m.personId}/awards/${awardId}`, { earnedOn }));
  return (
    <div className="youth-progress">
      {v.awards.length === 0 ? <p className="small muted">Agrega primero las insignias y rangos en su pestaña.</p> : null}
      <ul className="youth-awards">
        {v.awards.map((a) => (
          <li key={a.id}>
            <label className="check">
              <input
                type="checkbox"
                disabled={!v.canManage}
                checked={earned.has(a.id) || dating?.awardId === a.id}
                onChange={(e) => {
                  if (e.target.checked) setDating({ awardId: a.id, date: today() });
                  else if (dating?.awardId === a.id && !earned.has(a.id)) setDating(null);
                  else {
                    setDating(null);
                    void setDate(a.id, null);
                  }
                }}
              />
              <span>
                {a.name} <span className="small muted">({a.kind})</span>
              </span>
            </label>
            {earned.has(a.id) && dating?.awardId !== a.id ? (
              v.canManage ? (
                <button type="button" className="linklike small" aria-label={`Cambiar la fecha de ${a.name}`} onClick={() => setDating({ awardId: a.id, date: earned.get(a.id)! })}>
                  {' '}· {new Date(`${earned.get(a.id)}T12:00:00`).toLocaleDateString('es')}
                </button>
              ) : (
                <span className="small muted"> · {new Date(`${earned.get(a.id)}T12:00:00`).toLocaleDateString('es')}</span>
              )
            ) : null}
            {dating?.awardId === a.id ? (
              <span className="actions actions--wrap">
                <Field label={`Fecha de ${a.name}`} name="earnedOn" type="date" max={today()} value={dating.date} onChange={(e) => setDating({ awardId: a.id, date: e.target.value })} />
                <Button
                  variant="secondary"
                  disabled={!dating.date}
                  onClick={() => {
                    const date = dating.date;
                    setDating(null);
                    void setDate(a.id, date);
                  }}
                >
                  Guardar fecha
                </Button>
                <Button variant="ghost" onClick={() => setDating(null)}>
                  Cancelar
                </Button>
              </span>
            ) : null}
          </li>
        ))}
      </ul>
      {v.canManage ? (
        <div className="actions actions--wrap">
          <div className="field">
            <label htmlFor={`move-${m.personId}`}>{cap(info.group)}</label>
            <select id={`move-${m.personId}`} value={m.groupId ?? ''} onChange={(e) => void run(() => api.put(`${base}/members/${m.personId}`, { groupId: e.target.value || null }))}>
              <option value="">{info.none}</option>
              {v.groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </div>
          <Button variant="ghost" onClick={() => window.confirm(`¿Dar de baja a ${m.name}?`) && void run(() => api.delete(`${base}/members/${m.personId}`))}>
            Dar de baja
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function Groups({ base, v, info, run }: { base: string; v: View; info: Info; run: Run }) {
  function add(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const n = (k: string) => (String(f.get(k) ?? '').trim() ? Number(f.get(k)) : null);
    const body = { name: String(f.get('name') ?? '').trim(), minAge: n('minAge'), maxAge: n('maxAge') };
    e.currentTarget.reset();
    void run(() => api.post(`${base}/groups`, body));
  }
  return (
    <Card title={`${cap(info.groups)} por edad`}>
      {v.groups.length === 0 ? <p className="muted small">Crea {info.group === 'rama' ? 'las ramas o grupos' : 'los grupos'} por edad que usa tu distrito.</p> : null}
      <ul className="list">
        {v.groups.map((g) => (
          <li key={g.id} className="list__item list__item--actions">
            <span>
              <strong>{g.name}</strong> <span className="small muted">{ages(g)}</span>
            </span>
            {v.canManage ? (
              <Button variant="ghost" onClick={() => window.confirm(`¿Quitar ${info.group === 'rama' ? 'la' : 'el'} ${info.group} ${g.name}? Sus integrantes quedan «${info.none.toLowerCase()}».`) && void run(() => api.delete(`${base}/groups/${g.id}`))}>
                Quitar
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
      {v.canManage ? (
        <form className="form" onSubmit={add}>
          <Field label={`Nombre ${info.group === 'rama' ? 'de la' : 'del'} ${info.group}`} name="name" required minLength={2} maxLength={80} />
          <div className="form__row">
            <Field label="Edad desde" name="minAge" type="number" min={0} max={25} />
            <Field label="Edad hasta" name="maxAge" type="number" min={0} max={25} />
          </div>
          <Button type="submit" variant="secondary">
            Agregar {info.group}
          </Button>
        </form>
      ) : null}
    </Card>
  );
}

function Awards({ base, v, run }: { base: string; v: View; run: Run }) {
  function add(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const body = { kind: String(f.get('kind')), name: String(f.get('name') ?? '').trim(), description: String(f.get('description') ?? '').trim() || null };
    e.currentTarget.reset();
    void run(() => api.post(`${base}/awards`, body));
  }
  return (
    <Card title="Insignias y rangos">
      {v.awards.length === 0 ? <p className="muted small">Captura las insignias y rangos de tu manual para ir marcando el avance de cada quien.</p> : null}
      <ul className="list">
        {v.awards.map((a) => (
          <li key={a.id} className="list__item list__item--actions">
            <span>
              <strong>{a.name}</strong> <span className="badge">{a.kind === 'rango' ? 'Rango' : 'Insignia'}</span>
              {a.description ? <span className="small muted"> — {a.description}</span> : null}
            </span>
            {v.canManage ? (
              <Button variant="ghost" onClick={() => window.confirm(`¿Quitar «${a.name}»?`) && void run(() => api.delete(`${base}/awards/${a.id}`))}>
                Quitar
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
      {v.canManage ? (
        <form className="form" onSubmit={add}>
          <div className="form__row">
            <div className="field">
              <label htmlFor="award-kind">Tipo</label>
              <select id="award-kind" name="kind" defaultValue="insignia">
                <option value="insignia">Insignia</option>
                <option value="rango">Rango</option>
              </select>
            </div>
            <Field label="Nombre" name="name" required minLength={2} maxLength={80} />
          </div>
          <Field label="Descripción (opcional)" name="description" maxLength={500} />
          <Button type="submit" variant="secondary">
            Agregar
          </Button>
        </form>
      ) : null}
    </Card>
  );
}
